//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useTranslation } from 'react-i18next';
import noLicenseIllustration from '../../icons/no_license_illustration.png';
import loopColorLogo from '../../icons/loop_logo.png';
import { Button } from '../ui/button';

interface NoLicensePageProps {
  onClose: () => void;
  messageKey?: string;
}

const NoLicensePage: React.FC<NoLicensePageProps> = ({
  onClose,
  messageKey = 'no-license-message',
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="relative h-screen w-screen overflow-hidden bg-[#F2F2F2] dark:bg-[#161616]"
      id="id-no-license-container"
      data-testid="no-license-container"
    >
      {/* Center White Card */}
      <div
        className="absolute left-1/2 top-1/2 z-[1] flex h-[688px] max-h-[90vh] w-[776px] max-w-[95vw] -translate-x-1/2 -translate-y-1/2 flex-col items-center overflow-y-auto rounded-[10px] bg-white shadow-[0px_4px_4.3px_0px_rgba(149,149,149,0.1)] dark:bg-[#262626]"
        id="id-no-license-card"
        data-testid="no-license-card"
      >
        {/* Logo */}
        <div
          className="mt-[43px] flex justify-center"
          id="id-no-license-logo-wrapper"
          data-testid="no-license-logo-wrapper"
        >
          <img
            src={loopColorLogo}
            alt="DevOps Loop Logo"
            className="h-[216px] w-[462px] max-w-full object-contain"
          />
        </div>

        {/* Divider Line */}
        <div
          className="mt-[35px] h-px w-full bg-[#E4E4F0] dark:bg-gray-700"
          id="id-no-license-divider"
          data-testid="no-license-divider"
        />

        {/* Message Text */}
        <div
          className="mt-[54px] w-[448px] max-w-full px-4 text-center"
          id="id-no-license-message-wrapper"
          data-testid="no-license-message-wrapper"
        >
          <p className="m-0 font-inter text-[20px] font-normal leading-[29px] text-[#474747] dark:text-gray-300">
            {t(messageKey)}
          </p>
        </div>

        {/* Close Button */}
        <div
          className="mt-9"
          id="id-no-license-button-wrapper"
          data-testid="no-license-button-wrapper"
        >
          <Button
            onClick={onClose}
            className="rounded bg-[#3C91FF] px-6 py-2 text-sm font-normal text-white transition-colors hover:bg-[#2A7FE8] dark:bg-[#3C91FF] dark:hover:bg-[#4589ff]"
          >
            {t('Close')}
          </Button>
        </div>
      </div>

      {/* Left-Bottom Illustration — bottom aligns with card bottom */}
      <div
        className="absolute bottom-[calc(50%-344px+35px)] left-[-22px] hidden h-[653px] w-[668px] lg:block"
        id="id-no-license-illustration-wrapper"
        data-testid="no-license-illustration-wrapper"
      >
        <img
          src={noLicenseIllustration}
          alt="No License Illustration"
          className="block size-full object-contain object-left-bottom"
        />
      </div>
    </div>
  );
};

export default NoLicensePage;
